/**
 * Utility functions for formatting values
 */

/**
 * Format a number as currency
 * @param {number} value - Number to format
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };
  
  /**
   * Format a number as a percentage
   * @param {number} value - Number to format
   * @returns {string} - Formatted percentage string
   */
  export const formatPercent = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value / 100);
  };
  
  /**
   * Format a date as a string
   * @param {Date|number} date - Date object or timestamp
   * @param {string} format - Format type ('time', 'date', 'datetime', 'relative')
   * @returns {string} - Formatted date string
   */
  export const formatDate = (date, format = 'datetime') => {
    const dateObj = date instanceof Date ? date : new Date(date);
    
    switch (format) {
      case 'time':
        return dateObj.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        });
      
      case 'date':
        return dateObj.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
      
      case 'datetime':
        return dateObj.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      
      case 'relative':
        const now = new Date();
        const diffMs = now - dateObj;
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHour = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHour / 24);
        
        if (diffSec < 60) {
          return 'just now';
        } else if (diffMin < 60) {
          return `${diffMin}m ago`;
        } else if (diffHour < 24) {
          return `${diffHour}h ago`;
        } else if (diffDay < 7) {
          return `${diffDay}d ago`;
        } else {
          return formatDate(date, 'date');
        }
      
      default:
        return dateObj.toLocaleString();
    }
  };
  
  /**
   * Format a large number with abbreviations (K, M, B)
   * @param {number} value - Number to format
   * @returns {string} - Formatted number string
   */
  export const formatNumber = (value) => {
    if (value >= 1000000000) {
      return (value / 1000000000).toFixed(1) + 'B';
    } else if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(1) + 'K';
    } else {
      return value.toString();
    }
  };
  
  /**
   * Truncate text to a maximum length
   * @param {string} text - Text to truncate
   * @param {number} maxLength - Maximum length
   * @returns {string} - Truncated text
   */
  export const truncateText = (text, maxLength = 30) => {
    if (!text || text.length <= maxLength) {
      return text;
    }
    
    return text.substring(0, maxLength - 3) + '...';
  };